const all = new Map<string, GeckoTMinMethod>()

/**
 * An object representing a method of calculating T-mins (minimum allowable
 * thicknesses). This is required for all CDFs derived from T-mins.
 */
export class GeckoTMinMethod {
  //----------------------------------------------------------------------------

  public static readonly Description = 'How the T-min values were derived'

  //----------------------------------------------------------------------------

  public static readonly TMinByComponent = new GeckoTMinMethod(
    //currently used by floors and roofs
    'tmin-by-component',
    'T-min by component',
    'Derived from a single component-wide T-min'
  )
  public static readonly TMinByShellCourse = new GeckoTMinMethod(
    //currently used by vertical tanks, and cones
    'tmin-by-shell-course',
    'T-min by Course',
    'Derived from a T-min per course'
  )
  public static readonly TMinByWall = new GeckoTMinMethod(
    //only for boilers
    'tmin-by-wall',
    'Fixed T-min per wall',
    'Derived from a T-min per wall'
  )
  public static readonly TMinByApi653 = new GeckoTMinMethod(
    //
    'tmin-by-api-653',
    'T-min by Elevation',
    'Derived from API 653 calculations'
  )
  public static readonly TMinWithAnnularRing = new GeckoTMinMethod(
    'tmin-with-annular-ring',
    'T-min with Annular Ring',
    'Different Tmins for the annular ring and the center plates'
  )
  public static readonly TMinByApi570 = new GeckoTMinMethod(
    'tmin-by-api-570',
    'T-min by API 570',
    'Derived from API 570 calculations'
  )
  public static readonly TMinByApi510 = new GeckoTMinMethod(
    'tmin-by-api-510',
    'T-min by API 510',
    'Derived from API 510 calculations'
  )
  public static readonly TMinByShipDeck = new GeckoTMinMethod(
    'tmin-by-ship-deck',
    'T-min by Ship Deck',
    'Derived from 25% wastage threshold (75% of nominal thickness)'
  )
  public static readonly None = new GeckoTMinMethod(
    '',
    'None',
    'Not derived from a particular T-min value'
  )

  //----------------------------------------------------------------------------

  /** A unique ID for this t-min method */
  public readonly id: string

  /** A user-facing name for this object, or its ID if none is populated */
  public readonly name: string

  /** A user-facing description for this a specific t-min method */
  public readonly description: string

  private constructor(id: string, name: string, description: string) {
    this.id = id
    this.name = name
    this.description = description
    all.set(id, this)
  }

  //----------------------------------------------------------------------------

  /**
   * A default sorting method for component data frame t-min methods
   * @param a The first t-min method object
   * @param b The second t-min method object
   */
  public static DefaultSort(a: GeckoTMinMethod, b: GeckoTMinMethod): number {
    return defaultOrder.indexOf(a.id) - defaultOrder.indexOf(b.id)
  }

  /**
   * Returns the unique t-min method instance for a given ID if it exists
   * @param id The unique ID of this t-min method
   */
  public static GetByID(id: string): GeckoTMinMethod | undefined {
    return all.get(id)
  }

  /**
   * Returns the unique data t-min method instance for a given ID, or creates a
   * unique t-min method instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(id: string | undefined): GeckoTMinMethod {
    return id === undefined
      ? GeckoTMinMethod.None
      : (all.get(id) ?? new GeckoTMinMethod(id, id, ''))
  }
}

const defaultOrder = [
  GeckoTMinMethod.TMinByApi653.id,
  GeckoTMinMethod.TMinByComponent.id,
  GeckoTMinMethod.TMinByShellCourse.id,
]
